/**
 * File: AppRoot.tsx
 * Path: apps/admin/src/AppRoot.tsx
 * Description: Top-level component orchestrating global providers and the router.
 * Adheres to the Industrial design system tokens (dark background, gold primary).
 */

import React from "react";
import { QueryProvider } from "@/modules/app/components/QueryProvider";
import { AuthProvider } from "@/modules/auth/components/AuthProvider";
import { AppRouter } from "@/AppRouter";
import { Toaster } from "@idk/ui";
import { useColorScheme } from "@/modules/layout/utils/colorSchemeUtils";

/**
 * AppRoot composes the core infrastructure of the Admin SPA.
 * Styling is driven by HSL variables defined in @idk/ui/src/stylesheet/globals.css.
 */
export const AppRoot: React.FC = () => {
  const [colorScheme] = useColorScheme();
  return (
    <QueryProvider>
      <AuthProvider>
        <React.Suspense
          fallback={
            <div className="flex h-screen items-center justify-center bg-background text-primary">
              <div className="flex flex-col items-center gap-stack-md">
                {/* Spinner or logo can go here */}
                <span className="text-sm font-sans tracking-wider uppercase animate-pulse">
                  System Loading
                </span>
              </div>
            </div>
          }
        >
          {/* The AppRouter handles the rendering of specific pages.
            Global layout shells (e.g., AdminShell) should be implemented 
            within the route tree or as a layout component in src/app/layouts.
          */}
          <AppRouter />
        </React.Suspense>
        {/* Every toast.success/error in the admin renders here; without it they
            were silent. Top centre clears the phone's bottom navigation. */}
        <Toaster theme={colorScheme} position="top-center" />
      </AuthProvider>
    </QueryProvider>
  );
};
